import React, { useState, useEffect } from 'react';
import { Box, TextField, Stack, ToggleButton, ToggleButtonGroup, Typography, Button } from '@mui/material';
import { COLOR_PRIMARY_2 } from 'src/colors';
import { exchangeRates } from 'src/const';
import { formatCurrency } from 'src/utils/functions';

interface PriceRangeInputProps {
  label: string;
  range: { min: number; max: number };
  onRangeChange: (values: { min: string; max: string }) => void;
  currencySymbol: string;
  onClose: () => void;
}

const PriceRangeInput: React.FC<PriceRangeInputProps> = ({
  label,
  range,
  onRangeChange,
  currencySymbol,
  onClose,
}) => {
  // Local state for immediate updates
  const [budgetRange, setBudgetRange] = useState({
    min: range.min === 0 ? '' : range.min.toString(),
    max: range.max === 0 ? '' : range.max.toString(),
  });

  // Update local state when props change
  useEffect(() => {
    setBudgetRange({
      min: range.min === 0 ? '' : range.min.toString(),
      max: range.max === 0 ? '' : range.max.toString(),
    });
  }, [range.min, range.max]);

  const handleChange = (type: 'min' | 'max', value: string) => {
    setBudgetRange(prev => ({ ...prev, [type]: value }));
  };

  const handleApply = () => {
    // Update parent state with both values when Apply is clicked
    const values = {
      min: budgetRange.min === '' ? '0' : budgetRange.min,
      max: budgetRange.max === '' ? '0' : budgetRange.max
    };
    onRangeChange(values);
    onClose();
  };

  const handleClear = () => {
    // Clear local state
    setBudgetRange({ min: '', max: '' });
    // Clear parent state
    onRangeChange({ min: '0', max: '0' });
    onClose();
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            type="string"
            placeholder="Min"
            value={formatCurrency(String(budgetRange.min))}
            onChange={e => handleChange('min', e.target.value ? e.target.value.replace(/,/g, '') : '')}
            InputProps={{
              startAdornment: (
                <Typography sx={{ color: COLOR_PRIMARY_2, mr: 1 }}>{currencySymbol}</Typography>
              ),
            }}
          />
          <TextField
            size="small"
            type="string"
            placeholder="Max"
            value={formatCurrency(String(budgetRange.max))}
            onChange={e => handleChange('max', e.target.value ? e.target.value.replace(/,/g, '') : '')}
            InputProps={{
              startAdornment: (
                <Typography sx={{ color: COLOR_PRIMARY_2, mr: 1 }}>{currencySymbol}</Typography>
              ),
            }}
          />
        </Stack>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button 
            variant="outlined" 
            size="small"
            onClick={handleClear}
            sx={{ 
              width: '100px',
              borderColor: COLOR_PRIMARY_2,
              color: COLOR_PRIMARY_2,
              '&:hover': {
                borderColor: COLOR_PRIMARY_2,
                opacity: 0.9,
              },
            }}
          >
            Clear
          </Button>
          <Button 
            variant="contained" 
            size="small"
            onClick={handleApply}
            sx={{ 
              width: '100px',
              color: '#fff',
              backgroundColor: COLOR_PRIMARY_2,
              '&:hover': {
                backgroundColor: COLOR_PRIMARY_2,
                opacity: 0.9,
              },
            }}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

interface BudgetFilterContentProps {
  onBudgetChange: (budgetType: string, values: { min: number; max: number }) => void;
  initialBudget?: {
    buy?: { min: number; max: number };
    rent?: { min: number; max: number };
  };
  mode: 'buy' | 'rent';
  onModeChange: (mode: 'buy' | 'rent') => void;
  currency: string;
  onClose: () => void;
}

const BudgetFilterContent: React.FC<BudgetFilterContentProps> = ({
  onBudgetChange,
  initialBudget = { buy: { min: 0, max: 0 }, rent: { min: 0, max: 0 } },
  mode,
  onModeChange,
  currency,
  onClose,
}) => {
  const currencySymbol = exchangeRates[currency].symbol;

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: 'buy' | 'rent') => {
    if (newMode !== null) {
      onModeChange(newMode);
    }
  };

  const handleRangeChange = (values: { min: string; max: string }) => {
    const newRange = {
      min: values.min === '' ? 0 : Number(values.min),
      max: values.max === '' ? 0 : Number(values.max)
    };
    onBudgetChange(mode, newRange);
  };

  const currentRange = initialBudget[mode] || { min: 0, max: 0 };

  return (
    <Box sx={{ p: 2, minWidth: 300 }}>
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={handleModeChange}
        aria-label="budget mode"
        fullWidth
        sx={{ mb: 3 }}
      >
        <ToggleButton
          value="buy"
          aria-label="buy mode"
          sx={{
            '&.Mui-selected': { 
              backgroundColor: COLOR_PRIMARY_2,
              color: 'white',
              '&:hover': {
                backgroundColor: COLOR_PRIMARY_2,
                opacity: 0.9,
              },
            },
          }}
        >
          Buy
        </ToggleButton>
        <ToggleButton
          value="rent"
          aria-label="rent mode"
          sx={{
            '&.Mui-selected': {
              backgroundColor: COLOR_PRIMARY_2,
              color: 'white',
              '&:hover': {
                backgroundColor: COLOR_PRIMARY_2,
                opacity: 0.9,
              },
            },
          }}
        >
          Rent
        </ToggleButton>
      </ToggleButtonGroup>

      <PriceRangeInput
        label={mode === 'buy' ? "Property price" : "Monthly rent price"}
        range={currentRange}
        onRangeChange={handleRangeChange}
        currencySymbol={currencySymbol}
        onClose={onClose}
      />
    </Box>
  );
};

export default BudgetFilterContent;
